// homepage-map-view.ts — the pure half of HomepageSvgMap, the selector's adaptation of the homepage world map.
//
// SOURCE: public/design/sg-beta-v2.01.html, `_lTryDraw` / `_lDraw` / `_lRender` (the landing map). That runtime
// draws Natural Earth 110m land with `d3.geoNaturalEarth1().rotate([100, 0])`, pans/zooms it with `d3.zoom`
// (scaleExtent [1, 220], clickDistance 14, translateExtent = the drawn box) and scales each pin by
// `zf / k`. This module keeps that geometry React-free and browser-free so it is asserted in unit:
//
//   • the WORLD is one fixed box (WORLD_W × WORLD_H) the projection is fitted into ONCE. A view is the d3 zoom
//     transform {x, y, k} from world units to pane pixels, so a pane resize never re-projects anything — the
//     same geographic point stays under the same transform, which is what lets full screen keep its centre.
//   • `clampView` is d3-zoom's default `constrain` with translateExtent = the world box; the unit test runs the
//     vendored d3 and compares, so the programmatic views (fit, locate, carry) land exactly where d3 would.
//   • `HOME_PIN_GEO` is a COPY of the homepage roster's real lon/lat (`L_PINS`), byte-checked against the export.
//     It is the position of last resort for a listed mountain the served geo cannot place. Position only:
//     status and destination always come from the caller (the list's own facts).

import type { PinStatus } from "./pin-markers";

export const WORLD_W = 1000;
export const WORLD_H = 520;
/** `_lDraw`: `d3.zoom().scaleExtent([1, 220])`, relative to the whole world fitted to the pane. */
export const HOME_MAX_ZOOM = 220;
/** `_lDraw`: `.clickDistance(14)` — a press that travels further is a pan, never a pin pick. */
export const HOME_CLICK_DISTANCE = 14;
/** `L_KMAX` — the pin-growth curve's ceiling and the deepest a fit ever goes. */
export const HOME_KMAX = 26;
/** locate zooms to at least this multiple of the fitted scale — enough to separate the Colorado cluster. */
export const LOCATE_ZOOM = 3;

export interface Size { w: number; h: number }
export interface View { x: number; y: number; k: number }
export interface WorldPoint { x: number; y: number }

/** A pin before projection: real lon/lat plus the list's own facts. */
export interface GeoPin {
  key: string;
  name: string;
  lon: number;
  lat: number;
  status?: PinStatus;
  href?: string;
  /** where the coordinate came from: the served geo, or the homepage roster's copy. */
  source: "served" | "homepage";
}
export interface WorldPin extends GeoPin { w: WorldPoint }

// ── THE HOMEPAGE ROSTER'S COORDINATES (copied from `L_PINS`: slug → [lon, lat]) ─────────────────────────────
export const HOME_PIN_GEO: Readonly<Record<string, readonly [number, number]>> = {
  whistler: [-122.95, 50.11],
  big_white: [-118.93, 49.72],
  baldy: [-119.2364219, 49.1528412],
  sun_peaks: [-119.89, 50.88],
  silver_star: [-119.06, 50.36],
  revelstoke: [-118.16, 50.96],
  kicking_horse: [-117.05, 51.3],
  fernie: [-115.09, 49.46],
  panorama: [-116.24, 50.46],
  whitewater: [-117.15, 49.44],
  red: [-117.81, 49.11],
  banff_sunshine: [-115.78, 51.08],
  lake_louise: [-116.16, 51.44],
  marmot_basin: [-118.07, 52.8],
  blue: [-80.31, 44.5],
  tremblant: [-74.58, 46.21],
  big_sky: [-111.4, 45.28],
  jackson_hole: [-110.83, 43.59],
  vail: [-106.37, 39.64],
  keystone: [-105.94, 39.61],
  breckenridge: [-106.07, 39.48],
  copper_mt: [-106.15, 39.5],
  steamboat: [-106.8, 40.46],
  snowmass: [-106.95, 39.21],
  park_city: [-111.51, 40.65],
  deer_valley: [-111.48, 40.63],
  palisades: [-120.24, 39.2],
  heavenly: [-119.94, 38.94],
  mammoth: [-118.88, 37.63],
  stowe: [-72.78, 44.53],
  killington: [-72.8, 43.6],
  chamonix: [6.87, 45.92],
  val_thorens: [6.58, 45.3],
  courchevel: [6.63, 45.41],
  zermatt: [7.75, 46.02],
  verbier: [7.23, 46.1],
  st_anton: [10.27, 47.13],
  cortina: [12.14, 46.54],
  livigno: [10.14, 46.54],
  are: [13.08, 63.4],
  niseko: [140.68, 42.86],
  hakuba: [137.86, 36.7],
  queenstown: [168.66, -45.03],
  portillo: [-70.12, -32.83],
};

/** `L_PIN_ALIAS` — the three legacy URL slugs whose spelling is not the roster's. */
export const HOME_PIN_ALIAS: Readonly<Record<string, string>> = { "fernie-alpine": "fernie", "lake-louis": "lake_louise", "vail-sky": "vail" };

/** `L_PIN_BY_SLUG`: legacy URL code → the roster slug, or null. Never falls back to another mountain. */
export function homePinSlug(code: string | null | undefined): string | null {
  if (!code) return null;
  const k = HOME_PIN_ALIAS[code] ?? String(code).replace(/-/g, "_");
  if (HOME_PIN_GEO[k]) return k;
  return HOME_PIN_GEO[code] ? code : null;
}

/** The homepage roster's real [lon, lat] for a mountain code, or null when the roster does not hold it. */
export function homepageGeoFor(code: string | null | undefined): readonly [number, number] | null {
  const slug = homePinSlug(code);
  return slug ? (HOME_PIN_GEO[slug] ?? null) : null;
}

/** The subset of a served marker this map reads (structurally the selector's `KeylessMarker`). */
export interface ServedMarker {
  lat: number;
  lng: number;
  name?: unknown;
  key?: string;
  status?: PinStatus;
  href?: string;
  flag?: string;
}

const validLonLat = (lon: unknown, lat: unknown): boolean =>
  typeof lon === "number" && typeof lat === "number" && Number.isFinite(lon) && Number.isFinite(lat) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180;

/**
 * The selector's mountain pins: every served mountain marker with a real coordinate first (anywhere on Earth —
 * the world projection has no regional cut-off), then, for each listed resort the served geo did not place,
 * the homepage roster's coordinate for the same mountain. Airports and stops are not mountain pins. A resort the
 * roster does not hold either gets no pin — never a guessed position.
 */
export function resolveHomepagePins(
  markers: ServedMarker[],
  resorts: { href: string; label: string }[],
  codeOf: (href: string) => string | null,
  facts: (code: string, href: string) => { status?: PinStatus; href?: string },
): GeoPin[] {
  const out: GeoPin[] = [];
  const drawn = new Set<string>();
  for (const m of markers) {
    if (m.flag === "plane" || m.flag === "stop") continue;
    if (typeof m.name !== "string" || !m.name) continue;
    if (!validLonLat(m.lng, m.lat)) continue;
    const key = m.key ?? m.name;
    if (drawn.has(key)) continue;
    drawn.add(key);
    out.push({ key, name: m.name, lon: m.lng, lat: m.lat, status: m.status, href: m.href, source: "served" });
  }
  for (const r of resorts) {
    const code = codeOf(r.href);
    if (!code) continue;
    const key = `m:${code}`;
    if (drawn.has(key)) continue;
    const geo = homepageGeoFor(code);
    if (!geo) continue;
    drawn.add(key);
    out.push({ key, name: r.label.replace(/,\s*[A-Za-z]{2}$/, "").trim(), lon: geo[0], lat: geo[1], ...facts(code, r.href), source: "homepage" });
  }
  return out;
}

/** Project pins through the runtime's projection; a point the projection cannot place is dropped, not guessed. */
export function projectPins(pins: GeoPin[], project: (lon: number, lat: number) => WorldPoint | null): WorldPin[] {
  return pins.flatMap((p) => {
    const w = project(p.lon, p.lat);
    return w && Number.isFinite(w.x) && Number.isFinite(w.y) ? [{ ...p, w }] : [];
  });
}

// ── THE VIEW ─────────────────────────────────────────────────────────────────────────────────────────────────
/** The scale at which the whole world fits the pane — the homepage's `k = 1`. */
export const containScale = (b: Size) => Math.min(b.w / WORLD_W, b.h / WORLD_H);
/** d3 scaleExtent for this pane: [whole world, × HOME_MAX_ZOOM]. */
export const zoomLimits = (b: Size): [number, number] => [containScale(b), containScale(b) * HOME_MAX_ZOOM];
export const clampK = (k: number, b: Size) => {
  const [lo, hi] = zoomLimits(b);
  return Math.max(lo, Math.min(hi, k));
};
/** the homepage's relative zoom (1 = whole world in the pane). */
export const relativeZoom = (v: View, b: Size) => v.k / containScale(b);

/** d3-zoom's default `constrain`, extent = the pane, translateExtent = the world box. */
export function clampView(v: View, b: Size): View {
  const inv = (p: number, t: number) => (p - t) / v.k;
  const dx0 = inv(0, v.x) - 0;
  const dx1 = inv(b.w, v.x) - WORLD_W;
  const dy0 = inv(0, v.y) - 0;
  const dy1 = inv(b.h, v.y) - WORLD_H;
  const tx = dx1 > dx0 ? (dx0 + dx1) / 2 : Math.min(0, dx0) || Math.max(0, dx1);
  const ty = dy1 > dy0 ? (dy0 + dy1) / 2 : Math.min(0, dy0) || Math.max(0, dy1);
  return { x: v.x + v.k * tx, y: v.y + v.k * ty, k: v.k };
}

export const toScreen = (p: WorldPoint, v: View) => ({ sx: p.x * v.k + v.x, sy: p.y * v.k + v.y });
export const toWorld = (sx: number, sy: number, v: View): WorldPoint => ({ x: (sx - v.x) / v.k, y: (sy - v.y) / v.k });

/** room the fit leaves round the pins' anchors: the pin and its label rise above the anchor. */
export const FIT_PAD = { l: 36, r: 36, t: 64, b: 16 } as const;

/** Fit every pin in the pane (or the whole world when there are none), never deeper than `HOME_KMAX`. */
export function fitWorldPoints(points: WorldPoint[], b: Size): View {
  const kc = containScale(b);
  if (!points.length) return clampView({ k: kc, x: (b.w - WORLD_W * kc) / 2, y: (b.h - WORLD_H * kc) / 2 }, b);
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const p of points) {
    x0 = Math.min(x0, p.x); y0 = Math.min(y0, p.y); x1 = Math.max(x1, p.x); y1 = Math.max(y1, p.y);
  }
  const aw = Math.max(1, b.w - FIT_PAD.l - FIT_PAD.r);
  const ah = Math.max(1, b.h - FIT_PAD.t - FIT_PAD.b);
  const bw = x1 - x0;
  const bh = y1 - y0;
  const cap = kc * HOME_KMAX;
  const raw = Math.min(bw > 0 ? aw / bw : Infinity, bh > 0 ? ah / bh : Infinity);
  const k = Math.max(kc, Math.min(cap, Number.isFinite(raw) ? raw : kc * 8));
  const cx = FIT_PAD.l + aw / 2;
  const cy = FIT_PAD.t + ah / 2;
  return clampView({ k, x: cx - ((x0 + x1) / 2) * k, y: cy - ((y0 + y1) / 2) * k }, b);
}

/** Centre a world point at scale k (clamped), as the homepage's `_lFocus` does. */
export function centerOn(p: WorldPoint, k: number, b: Size): View {
  const kk = clampK(k, b);
  return clampView({ k: kk, x: b.w / 2 - p.x * kk, y: b.h / 2 - p.y * kk }, b);
}

/** Zoom by `factor` keeping the pane point (cx, cy) fixed — d3's `scaleBy` about a point. */
export function zoomAround(v: View, factor: number, cx: number, cy: number, b: Size): View {
  const k = clampK(v.k * factor, b);
  const p = toWorld(cx, cy, v);
  return clampView({ k, x: cx - p.x * k, y: cy - p.y * k }, b);
}

/**
 * Carry a view EXACTLY to a new pane size: the geographic point at the old centre is at the new centre, at the
 * same scale — never clamped to the destination. Used for full screen enter/exit, where keeping the view is the
 * contract even when the new pane then shows white beyond the world's edge (a low zoom, or a view panned to an
 * edge). Clamping here is what used to move the centre and change the zoom on entry and exit.
 */
export function carryView(v: View, prev: Size, next: Size): View {
  const c = toWorld(prev.w / 2, prev.h / 2, v);
  return { k: v.k, x: next.w / 2 - c.x * v.k, y: next.h / 2 - c.y * v.k };
}

/** An ordinary resize of a touched map: carry the centre and scale, then keep the world covering the new pane. */
export function carryViewWithin(v: View, prev: Size, next: Size): View {
  const c = toWorld(prev.w / 2, prev.h / 2, v);
  const k = clampK(v.k, next);
  return clampView({ k, x: next.w / 2 - c.x * k, y: next.h / 2 - c.y * k }, next);
}

export const sameView = (a: View, b: View, eps = 1e-6) => Math.abs(a.x - b.x) < eps && Math.abs(a.y - b.y) < eps && Math.abs(a.k - b.k) < eps;

// ── THE PINS' DRAWN SIZE AND LABELS (`_lRender`) ─────────────────────────────────────────────────────────────
/** `zf = Math.min(1.6, 0.78 + 0.82 * Math.log(Math.max(1, k)) / Math.log(L_KMAX))`, k = relative zoom. */
export const pinScale = (kr: number) => Math.min(1.6, 0.78 + (0.82 * Math.log(Math.max(1, kr))) / Math.log(HOME_KMAX));
/** `_lRender`: a lit label is fully shown; at rest labels appear from k ≥ 2.2 at 0.85. */
export const labelOpacity = (kr: number, lit: boolean) => (lit ? 1 : kr >= 2.2 ? 0.85 : 0);
/** `.lbl` sits at y = −38 in the pin's own units. */
export const LABEL_Y = -38;
const LABEL_CHAR_W = 7.2;
const LABEL_H = 13;

/**
 * Which labels the homepage rule shows that do not collide with an already-placed one or leave the pane. Lit
 * pins place first, then bookable, then information-only, then unknown; the name always stays in aria-label.
 */
export function placeHomeLabels(pins: WorldPin[], v: View, b: Size, lit: ReadonlySet<string>): Set<string> {
  const kr = relativeZoom(v, b);
  const zf = pinScale(kr);
  const rank = (p: WorldPin) => (lit.has(p.key) ? 0 : p.status === "bookable" ? 1 : p.status === "info-only" ? 2 : 3);
  const order = pins.map((p, i) => ({ p, i })).sort((a, c) => rank(a.p) - rank(c.p) || a.i - c.i);
  const placed: { x0: number; y0: number; x1: number; y1: number }[] = [];
  const shown = new Set<string>();
  for (const { p } of order) {
    if (labelOpacity(kr, lit.has(p.key)) <= 0) continue;
    const { sx, sy } = toScreen(p.w, v);
    const w = (p.name.length * LABEL_CHAR_W + 4) * zf;
    const h = LABEL_H * zf;
    const cy = sy + LABEL_Y * zf;
    const r = { x0: sx - w / 2, y0: cy - h, x1: sx + w / 2, y1: cy + 2 * zf };
    if (r.x0 < 0 || r.x1 > b.w || r.y0 < 0 || r.y1 > b.h) continue;
    if (placed.some((q) => r.x0 < q.x1 && r.x1 > q.x0 && r.y0 < q.y1 && r.y1 > q.y0)) continue;
    placed.push(r);
    shown.add(p.key);
  }
  return shown;
}

/** A press that travelled past the homepage's click distance (or became a pinch) is a pan: its click is eaten. */
export function isDragGesture(start: { x: number; y: number } | null, now: { x: number; y: number }, slop = HOME_CLICK_DISTANCE): boolean {
  if (!start) return false;
  return Math.hypot(now.x - start.x, now.y - start.y) > slop;
}

// ── THE RUNTIME (built from the vendored d3 + topojson + world-110m; injected so tests run the real files) ────
export interface HomeMapRuntime {
  /** land outlines in world units, Antarctica excluded (as `_lDraw` does). */
  land: string[];
  project: (lon: number, lat: number) => WorldPoint | null;
}
type ProjectionLike = ((p: [number, number]) => [number, number] | null) & {
  rotate(r: [number, number]): ProjectionLike;
  fitSize(size: [number, number], obj: unknown): ProjectionLike;
};
export interface HomeD3Geo {
  geoNaturalEarth1(): ProjectionLike;
  geoPath(p: ProjectionLike): (f: unknown) => string | null;
}
export interface HomeTopojson {
  feature(topo: unknown, obj: unknown): { features: { properties?: { name?: string } }[] };
}

/** `_lDraw`'s geography, fitted once into the world box. Throws when a dependency is missing (fail closed). */
export function buildHomeMapRuntime(d3: HomeD3Geo | undefined, topojson: HomeTopojson | undefined, topo: unknown): HomeMapRuntime {
  const objects = (topo as { objects?: { countries?: unknown } } | null | undefined)?.objects;
  if (!d3?.geoNaturalEarth1 || !d3.geoPath || !topojson?.feature || !objects?.countries) {
    throw new Error("homepage map runtime unavailable");
  }
  const feats = topojson.feature(topo, objects.countries).features.filter((f) => f.properties?.name !== "Antarctica");
  const proj = d3.geoNaturalEarth1().rotate([100, 0]).fitSize([WORLD_W, WORLD_H], { type: "Sphere" });
  const path = d3.geoPath(proj);
  const land = feats.map((f) => path(f)).filter((d): d is string => typeof d === "string" && d.length > 0);
  return {
    land,
    project: (lon, lat) => {
      const xy = proj([lon, lat]);
      return xy && Number.isFinite(xy[0]) && Number.isFinite(xy[1]) ? { x: xy[0], y: xy[1] } : null;
    },
  };
}

// ── THE VIEW CONTROLLER ──────────────────────────────────────────────────────────────────────────────────────
// HomepageSvgMap's decisions about WHEN and WHERE the view moves, held outside React so the exact sequence the
// component runs (measure → transition carry → at-rest fit → gestures) is driven in unit against the real d3
// zoom. The component owns only the DOM: it feeds measurements, fits and d3 zoom events in, and applies what
// comes out through d3's own `zoom.transform` (see `applyHomeView`).

/** How a view is applied. `exact`: a fullscreen carry — no clamp of any kind, the view is shown as given. */
export type HomeViewApplier = (next: View, opts: { ms: number; box: Size; exact: boolean }) => void;

/** A fullscreen transition waiting for its resize: carry centre+zoom, or restore the pane's untouched view. */
type Pending = { kind: "carry" } | { kind: "restore"; view: View; box: Size };
const boxKey = (b: Size) => `${b.w}x${b.h}`;

export class HomeMapController {
  /** the measured pane (null until a non-zero measurement). */
  box: Size | null;
  /** the view on screen, as last reported by `shown`. */
  view: View | null = null;
  /** the at-rest fit for the current box and pins, as last reported by `fitChanged`. */
  fit: View | null = null;
  /** true until a gesture, a button or a locate; at rest, an ordinary resize refits. */
  atRest = true;
  interactions = 0;
  private pending: Pending | null = null;
  private entry: { view: View; box: Size; n: number } | null = null;
  /**
   * ONE-SHOT: the box a fullscreen transition carried into, and (once seen) the fit the carry stands in for. The
   * at-rest fit for exactly that transition is skipped once; React re-running the same effect with the same fit is
   * still the same transition. Any later measurement, a different fit (pins changed) or "show all" clears it.
   */
  private suppress: { box: string; fit: View | null | undefined } | null = null;

  private readonly apply: HomeViewApplier;

  constructor(apply: HomeViewApplier, initialBox: Size | null = null) {
    this.apply = apply;
    this.box = initialBox;
  }

  current(): View | null {
    return this.view ?? this.fit;
  }

  /** A view is on screen. `user` = it came from a gesture (d3's sourceEvent), which ends "at rest". */
  shown(v: View, user: boolean): void {
    this.view = v;
    if (user) this.touch();
  }

  /** The pane was measured. Returns true when the box changed (the component then re-renders at that size). */
  measure(next: Size): boolean {
    if (!(next.w > 0 && next.h > 0)) return false; // hidden / not laid out: defer
    const prev = this.box;
    if (prev?.w === next.w && prev.h === next.h) return false;
    this.box = next;
    this.suppress = null;
    const p = this.pending;
    this.pending = null;
    const cur = this.view;
    if (!prev || !cur) return true; // first measurement: the at-rest fit places the view
    if (p) {
      const exact = p.kind === "restore" && p.box.w === next.w && p.box.h === next.h;
      this.suppress = { box: boxKey(next), fit: undefined };
      this.apply(exact ? p.view : carryView(cur, prev, next), { ms: 0, box: next, exact: true });
    } else if (!this.atRest) {
      this.apply(carryViewWithin(cur, prev, next), { ms: 0, box: next, exact: false });
    }
    return true;
  }

  /** The at-rest fit for the current box/pins was (re)computed. At rest, it is applied — except, once, the fit a
   *  fullscreen transition carry stands in for. */
  fitChanged(fit: View | null): void {
    this.fit = fit;
    const b = this.box;
    if (!fit || !b || !this.atRest) return;
    const s = this.suppress;
    if (s?.box === boxKey(b)) {
      if (s.fit === undefined) { s.fit = fit; return; }
      if (s.fit === fit) return;
    }
    this.suppress = null;
    this.apply(fit, { ms: 0, box: b, exact: false });
  }

  enterFull(): void {
    const cur = this.current();
    this.entry = cur && this.box ? { view: cur, box: this.box, n: this.interactions } : null;
    this.pending = { kind: "carry" };
  }

  /** Untouched since entry → restore the pane's exact view; touched → carry full screen's centre and scale back. */
  exitFull(): void {
    const e = this.entry;
    this.pending = e && e.n === this.interactions ? { kind: "restore", view: e.view, box: e.box } : { kind: "carry" };
    this.entry = null;
  }

  /** A transition whose resize never came (same size) must not make a later ordinary resize carry. */
  clearPending(): void {
    this.pending = null;
  }

  zoomBy(factor: number): void {
    const b = this.box;
    const cur = this.current();
    if (!b || !cur) return;
    this.touch();
    this.apply(zoomAround(cur, factor, b.w / 2, b.h / 2, b), { ms: 320, box: b, exact: false });
  }

  locate(p: WorldPoint): void {
    const b = this.box;
    const f = this.fit;
    if (!b || !f) return;
    const cur = this.current() ?? f;
    this.touch();
    this.apply(centerOn(p, Math.max(cur.k, f.k * LOCATE_ZOOM), b), { ms: 760, box: b, exact: false });
  }

  fitAll(): void {
    const f = this.fit;
    const b = this.box;
    if (!f || !b) return;
    this.atRest = true;
    this.suppress = null;
    this.interactions += 1;
    this.apply(f, { ms: 700, box: b, exact: false });
  }

  private touch(): void {
    this.atRest = false;
    this.interactions += 1;
  }
}
